import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { GET_SUSPICIOUS_BALANCE_ACTIVITY, GET_USER_BALANCE_CHANGES } from './graphql';

type ActivityRow = {
  userId: string;
  resource: string;
  source: string;
  count: number;
  totalDelta: number;
  firstAt: string;
  lastAt: string;
  avgGapSeconds: number;
};

type ChangeRow = {
  id: string;
  resource: string;
  delta: number | null;
  balanceAfter: number;
  source: string;
  created: string;
};

const RESOURCES = ['', 'DIAMONDS', 'COINS', 'TOURNAMENT_TICKETS', 'FREE_SPINS', 'ACCOLADES', 'STAMP_CARDS'];

// Mirrors TRUSTED_SOURCE_PATTERN on the backend (receipt-validated flows, admin tools).
const TRUSTED_SOURCE_PATTERN =
  /processStorePurchase|processPiggyBankPurchase|processSubscriptionDaysPurchase|processStarterPackStorePurchase|processSaleBundlePackStorePurchase|processChainedOfferPackStorePurchase|liveOps/;

const formatDate = (value: string) => (value ? new Date(value).toLocaleString() : '');

const BalanceWatch = () => {
  const [lookbackHours, setLookbackHours] = useState('24');
  const [minCount, setMinCount] = useState('5');
  const [maxAvgGapSeconds, setMaxAvgGapSeconds] = useState('60');
  const [resource, setResource] = useState('');
  const [copied, setCopied] = useState(false);
  const [detailUser, setDetailUser] = useState<string | null>(null);

  const [runSearch, { data, loading, error }] = useLazyQuery(GET_SUSPICIOUS_BALANCE_ACTIVITY, {
    fetchPolicy: 'network-only',
  });
  const [loadDetail, { data: detailData, loading: detailLoading, error: detailError }] = useLazyQuery(
    GET_USER_BALANCE_CHANGES,
    { fetchPolicy: 'network-only' }
  );

  const search = () => {
    setCopied(false);
    runSearch({
      variables: {
        lookbackHours: parseInt(lookbackHours, 10) || 24,
        minCount: parseInt(minCount, 10) || 5,
        maxAvgGapSeconds: parseInt(maxAvgGapSeconds, 10) || 60,
        resource: resource || null,
      },
    });
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: ActivityRow[] = data?.liveOps?.suspiciousBalanceActivity ?? [];
  const detailRows: ChangeRow[] = detailData?.liveOps?.userBalanceChanges ?? [];

  const openDetail = (userId: string) => {
    setDetailUser(userId);
    loadDetail({ variables: { userId, limit: 200 } });
  };

  const copyUserIds = async () => {
    const ids = Array.from(new Set(rows.map((row) => row.userId)));
    await navigator.clipboard.writeText(ids.join('\n'));
    setCopied(true);
  };

  return (
    <Card className="m-4">
      <Card.Header>Balance Watch - suspicious balance changes</Card.Header>
      <Card.Body>
        <Alert variant="info" className="small">
          Groups of positive balance changes (income) per user + source that happened suspiciously close together.
          Server-side ledger, cheats cannot bypass it. Feed flagged user IDs into Diamond Deduction for clawback.
        </Alert>
        <Row className="align-items-end mb-3">
          <Col xs="auto">
            <Form.Label className="small mb-1">Lookback (hours)</Form.Label>
            <Form.Control size="sm" value={lookbackHours} onChange={(e) => setLookbackHours(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Form.Label className="small mb-1">Min changes</Form.Label>
            <Form.Control size="sm" value={minCount} onChange={(e) => setMinCount(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Form.Label className="small mb-1">Max avg gap (seconds)</Form.Label>
            <Form.Control size="sm" value={maxAvgGapSeconds} onChange={(e) => setMaxAvgGapSeconds(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Form.Label className="small mb-1">Resource</Form.Label>
            <Form.Control as="select" size="sm" value={resource} onChange={(e) => setResource(e.target.value)}>
              {RESOURCES.map((value) => (
                <option key={value} value={value}>
                  {value || 'All resources'}
                </option>
              ))}
            </Form.Control>
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="primary" disabled={loading} onClick={search}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="outline-secondary" disabled={rows.length === 0} onClick={copyUserIds}>
              {copied ? 'Copied!' : `Copy ${new Set(rows.map((r) => r.userId)).size} user IDs`}
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error.message}</Alert>}
        {loading && <Spinner animation="border" />}
        {!loading && rows.length === 0 && !error && (
          <Alert variant="success" className="small">
            No suspicious activity for these filters.
          </Alert>
        )}

        {rows.length > 0 && (
          <Table size="sm" striped hover responsive>
            <thead>
              <tr>
                <th>User</th>
                <th>Resource</th>
                <th>Source</th>
                <th className="text-right">Changes</th>
                <th className="text-right">Total gained</th>
                <th className="text-right">Avg gap (s)</th>
                <th>First</th>
                <th>Last</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.userId}:${row.resource}:${row.source}`}>
                  <td>
                    <a href={`/sp3/devtools/userview/${row.userId}`} target="_blank" rel="noreferrer">
                      {row.userId}
                    </a>
                  </td>
                  <td>
                    <Badge variant={row.resource === 'DIAMONDS' ? 'danger' : 'secondary'}>{row.resource}</Badge>
                  </td>
                  <td>
                    <code className="small">{row.source}</code>{' '}
                    {TRUSTED_SOURCE_PATTERN.test(row.source) && <Badge variant="light">trusted (IAP/admin)</Badge>}
                  </td>
                  <td className="text-right">{row.count}</td>
                  <td className="text-right">+{row.totalDelta}</td>
                  <td className="text-right">{row.avgGapSeconds}</td>
                  <td className="small">{formatDate(row.firstAt)}</td>
                  <td className="small">{formatDate(row.lastAt)}</td>
                  <td>
                    <Button size="sm" variant="outline-primary" onClick={() => openDetail(row.userId)}>
                      History
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Modal show={!!detailUser} onHide={() => setDetailUser(null)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title className="h6">Balance history - {detailUser}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailError && <Alert variant="danger">{detailError.message}</Alert>}
            {detailLoading ? (
              <Spinner animation="border" />
            ) : (
              <Table size="sm" striped responsive>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Resource</th>
                    <th className="text-right">Delta</th>
                    <th className="text-right">Balance after</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((row) => (
                    <tr key={row.id}>
                      <td className="small">{formatDate(row.created)}</td>
                      <td>
                        <Badge variant={row.resource === 'DIAMONDS' ? 'danger' : 'secondary'}>{row.resource}</Badge>
                      </td>
                      <td className={`text-right ${row.delta !== null && row.delta > 0 ? 'text-success' : ''}`}>
                        {row.delta === null ? '?' : row.delta > 0 ? `+${row.delta}` : row.delta}
                      </td>
                      <td className="text-right">{row.balanceAfter}</td>
                      <td>
                        <code className="small">{row.source}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default BalanceWatch;
